import { getApiBaseUrl, getStreamApiUrl } from "@/config/runtime";
import type { SSEEvent, SSEEventType } from "@/types";

/**
 * Custom error class for API errors
 */
export class ApiError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    public data?: unknown,
  ) {
    super(`API Error: ${status} ${statusText}`);
    this.name = "ApiError";
  }
}

/**
 * Extract a human-readable message from an error thrown by the API layer.
 *
 * Handles FastAPI's two response shapes:
 * - `{ detail: "some string" }` (HTTPException) → returns the string
 * - `{ detail: [{ loc, msg, ... }] }` (422 validation) → returns the joined `msg`s
 *
 * Falls back to the provided default for non-API errors or unrecognized shapes.
 */
export function getApiErrorMessage(err: unknown, fallback = "Something went wrong"): string {
  if (err instanceof ApiError) {
    const detail = (err.data as { detail?: unknown } | undefined)?.detail;
    if (typeof detail === "string" && detail.trim()) {
      return detail;
    }
    if (Array.isArray(detail)) {
      const messages = detail
        .map((d) => (typeof d === "object" && d && "msg" in d ? String((d as { msg: unknown }).msg) : ""))
        .filter(Boolean);
      if (messages.length > 0) {
        return messages.join("; ");
      }
    }
  }
  if (err instanceof Error && err.message && !err.message.startsWith("API Error:")) {
    return err.message;
  }
  return fallback;
}

// Token getter function - will be set by AuthContext
let getAccessToken: (() => Promise<string>) | null = null;

export function setTokenGetter(getter: () => Promise<string>) {
  getAccessToken = getter;
}

/**
 * Acquire the current access token for use where headers aren't available
 * (e.g. a browser WebSocket URL query param). Returns an empty string if no
 * token getter is registered or acquisition fails.
 */
export async function getAccessTokenForWs(): Promise<string> {
  if (!getAccessToken) return "";
  try {
    return (await getAccessToken()) || "";
  } catch {
    return "";
  }
}

/**
 * Get authorization headers
 */
async function getHeaders(): Promise<Headers> {
  const headers = new Headers({
    "Content-Type": "application/json",
  });

  if (getAccessToken) {
    try {
      const token = await getAccessToken();
      if (token) {
        headers.set("Authorization", `Bearer ${token}`);
      }
    } catch (error) {
      // "No authenticated account" is a transient condition during login
      // transitions — log at warn level instead of error to reduce noise.
      const msg = error instanceof Error ? error.message : String(error);
      if (msg.includes("No authenticated account")) {
        console.warn("Token not yet available (auth in progress)");
      } else {
        console.error("Failed to get access token:", error);
      }
    }
  }

  return headers;
}

// Export getHeaders for use in services
export { getHeaders };

/**
 * Get the API base URL
 */
function getBaseUrl(): string {
  return getApiBaseUrl();
}

/**
 * Get the streaming API base URL (direct to backend, bypassing proxy)
 */
function getStreamUrl(): string {
  return getStreamApiUrl();
}

/**
 * Generic fetch wrapper with error handling
 */
async function fetchApi<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers = await getHeaders();

  // Merge headers
  if (options.headers) {
    const optHeaders = new Headers(options.headers);
    optHeaders.forEach((value, key) => headers.set(key, value));
  }

  const response = await fetch(`${getBaseUrl()}${path}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    let data: unknown;
    try {
      data = await response.json();
    } catch {
      // Response body is not JSON
    }
    throw new ApiError(response.status, response.statusText, data);
  }

  // Handle empty responses
  const text = await response.text();
  if (!text) {
    return undefined as T;
  }

  return JSON.parse(text) as T;
}

/**
 * Parse a POST-based SSE stream (event: + data: pairs, no blank-line boundary needed).
 */
async function* parseSSEStream(body: ReadableStream<Uint8Array>): AsyncGenerator<SSEEvent> {
  // NOSONAR -- SSE parsing loop with inherent branching
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let currentEvent: SSEEventType | null = null;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop() || "";

    for (const line of lines) {
      if (line.startsWith("event: ")) {
        currentEvent = line.slice(7).trim() as SSEEventType;
      } else if (line.startsWith("data: ") && currentEvent) {
        try {
          const data = JSON.parse(line.slice(6));
          yield { event: currentEvent, data };
        } catch {
          // Skip malformed JSON
        }
        currentEvent = null;
      }
    }
  }
}

/**
 * Fetch an SSE endpoint with proper headers and validation.
 */
async function fetchSSE(url: string, controller: AbortController): Promise<Response> {
  const headers = await getHeaders();
  headers.set("Accept", "text/event-stream");
  headers.delete("Content-Type");

  const response = await fetch(url, {
    method: "GET",
    headers,
    signal: controller.signal,
  });

  if (!response.ok) {
    let data: unknown;
    try {
      data = await response.json();
    } catch {
      // Response body is not JSON
    }
    throw new ApiError(response.status, response.statusText, data);
  }

  if (!response.body) {
    throw new Error("Response body is null");
  }

  return response;
}

/**
 * Read a GET-based SSE stream, dispatching parsed events via callback.
 */
async function readSSEGetStream(
  // NOSONAR -- SSE parsing loop with inherent branching
  body: ReadableStream<Uint8Array>,
  onEvent: (event: string, data: unknown) => void,
): Promise<void> {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let currentEvent: string | null = null;
  let dataLines: string[] = [];

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop() || "";

    for (const line of lines) {
      if (line.startsWith("event: ")) {
        currentEvent = line.slice(7).trim();
      } else if (line.startsWith("data: ")) {
        dataLines.push(line.slice(6));
      } else if (line.trim() === "" && currentEvent && dataLines.length > 0) {
        try {
          const data = JSON.parse(dataLines.join("\n"));
          onEvent(currentEvent, data);
        } catch {
          // Skip malformed JSON
        }
        currentEvent = null;
        dataLines = [];
      }
    }
  }
}

/**
 * API client with typed methods
 */
export const api = {
  /**
   * GET request
   */
  async get<T>(path: string, options?: { signal?: AbortSignal }): Promise<T> {
    return fetchApi<T>(path, { method: "GET", signal: options?.signal });
  },

  /**
   * POST request
   */
  async post<T>(path: string, body?: unknown, options?: { signal?: AbortSignal }): Promise<T> {
    return fetchApi<T>(path, {
      method: "POST",
      body: body ? JSON.stringify(body) : undefined,
      signal: options?.signal,
    });
  },

  /**
   * PUT request
   */
  async put<T>(path: string, body?: unknown): Promise<T> {
    return fetchApi<T>(path, {
      method: "PUT",
      body: body ? JSON.stringify(body) : undefined,
    });
  },

  /**
   * PATCH request
   */
  async patch<T>(path: string, body?: unknown): Promise<T> {
    return fetchApi<T>(path, {
      method: "PATCH",
      body: body ? JSON.stringify(body) : undefined,
    });
  },

  /**
   * DELETE request
   */
  async delete<T>(path: string, body?: unknown): Promise<T> {
    return fetchApi<T>(path, {
      method: "DELETE",
      body: body ? JSON.stringify(body) : undefined,
    });
  },

  /**
   * Upload files with multipart form data
   */
  async upload<T>(path: string, files: File[], additionalData?: Record<string, string>): Promise<T> {
    const headers = await getHeaders();
    headers.delete("Content-Type"); // Let browser set Content-Type with boundary

    const formData = new FormData();
    files.forEach((file) => formData.append("files", file));

    if (additionalData) {
      Object.entries(additionalData).forEach(([key, value]) => {
        formData.append(key, value);
      });
    }

    const response = await fetch(`${getBaseUrl()}${path}`, {
      method: "POST",
      headers,
      body: formData,
    });

    if (!response.ok) {
      let data: unknown;
      try {
        data = await response.json();
      } catch {
        // Response body is not JSON
      }
      throw new ApiError(response.status, response.statusText, data);
    }

    return response.json();
  },

  /**
   * Upload a single Blob/File as multipart form data.
   *
   * Unlike `upload()` which sends an array under the "files" field, this
   * sends a single blob under a configurable field name (default "file").
   */
  async uploadBlob<T>(
    path: string,
    blob: Blob,
    filename: string,
    fieldName = "file",
    extraFields?: Record<string, string>,
  ): Promise<T> {
    const headers = await getHeaders();
    headers.delete("Content-Type"); // Let browser set Content-Type with boundary

    const formData = new FormData();
    formData.append(fieldName, blob, filename);
    if (extraFields) {
      for (const [k, v] of Object.entries(extraFields)) {
        formData.append(k, v);
      }
    }

    const response = await fetch(`${getBaseUrl()}${path}`, {
      method: "POST",
      headers,
      body: formData,
    });

    if (!response.ok) {
      let data: unknown;
      try {
        data = await response.json();
      } catch {
        // Response body is not JSON
      }
      throw new ApiError(response.status, response.statusText, data);
    }

    return response.json();
  },

  /**
   * SSE streaming request
   */
  async *stream(path: string, body?: unknown): AsyncGenerator<SSEEvent> {
    const headers = await getHeaders();

    const response = await fetch(`${getStreamUrl()}${path}`, {
      method: "POST",
      headers,
      body: body ? JSON.stringify(body) : undefined,
    });

    if (!response.ok) {
      throw new ApiError(response.status, response.statusText);
    }

    if (!response.body) {
      throw new Error("Response body is null");
    }

    yield* parseSSEStream(response.body);
  },

  /**
   * GET-based SSE streaming request (for EventSource-like connections).
   * Used by the notification stream endpoint.
   * Returns an object with an async iterator and an abort function.
   */
  streamGet(
    path: string,
    onEvent: (event: string, data: unknown) => void,
    onError?: (error: unknown) => void,
  ): { abort: () => void } {
    const controller = new AbortController();

    (async () => {
      try {
        const response = await fetchSSE(`${getStreamUrl()}${path}`, controller);
        await readSSEGetStream(response.body!, onEvent);
      } catch (error) {
        if ((error as Error).name !== "AbortError") {
          onError?.(error);
        }
      }
    })();

    return { abort: () => controller.abort() };
  },
};
